import { Subscribe } from "@tanstack/react-table";
import type { ViewTableRow } from "./core/table";
import { Button } from "@design-system/Button/Button";
import { TriangleIcon } from "@icons";
import classNames from "classnames";
import { ViewTableCell } from "./ViewTableCell";
import styles from "./ExpandCollapseCell.module.scss";

interface Props<TData extends { id: string | number }> {
    row: ViewTableRow<TData>;
    children: React.ReactNode;
    hideDisclosureOnLeafChildren?: boolean;
}

const renderExpandCollapseCell = <TData extends { id: string | number }>({
    row,
    children,
    hideDisclosureOnLeafChildren = false,
}: Props<TData>) => {
    const canExpand = row.getCanExpand();
    const isExpanded = row.getIsExpanded();
    const shouldHideDisclosure =
        hideDisclosureOnLeafChildren && !canExpand && row.depth > 0;

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        row.toggleExpanded();
    };
    const indentMargin = `calc(var(--view-table-nesting-indent) * ${row.depth})`;

    return (
        <ViewTableCell>
            <div
                className={styles.expandContainer}
                style={
                    shouldHideDisclosure
                        ? { marginLeft: indentMargin }
                        : undefined
                }
            >
                {!shouldHideDisclosure && (
                    <div
                        className={styles.disclosureArea}
                        style={{ marginLeft: indentMargin }}
                    >
                        <Button
                            onClick={handleExpandClick}
                            iconOnly
                            aria-label={
                                isExpanded ? "Collapse row" : "Expand row"
                            }
                            aria-expanded={isExpanded}
                            variant="secondary"
                            style={{
                                visibility: canExpand ? "visible" : "hidden",
                            }}
                            className={classNames(styles.button, {
                                [styles.canExpand]: canExpand,
                            })}
                            size="sm"
                        >
                            <TriangleIcon
                                className="icon-color-gray-500"
                                style={{
                                    transform: isExpanded
                                        ? "rotate(0deg)"
                                        : "rotate(-90deg)",
                                }}
                            />
                        </Button>
                    </div>
                )}
                <div className={styles.content}>{children}</div>
            </div>
        </ViewTableCell>
    );
};

/**
 * TanStack keeps `row` stable while its expansion changes inside it, so the
 * expansion getters run in a `Subscribe` render function, which React
 * Compiler doesn't cache.
 */
export const ExpandCollapseCell = <TData extends { id: string | number }>({
    row,
    children,
    hideDisclosureOnLeafChildren,
}: Props<TData>) => (
    <Subscribe source={row.table.atoms.expanded}>
        {() =>
            renderExpandCollapseCell({
                row,
                children,
                hideDisclosureOnLeafChildren,
            })
        }
    </Subscribe>
);
ExpandCollapseCell.displayName = "ExpandCollapseCell";
